import React, { useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { TrashIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface Client {
  id: number;
  cuid: string;
  email: string;
  name: string;
  integrationId: number | null;
  accessToken: string | null;
  tokenExpire: number | null;
  clientID: string | null;
  clientSecret: string | null;
  ApiKey: string | null;
  AppKey: string | null;
  createdAt: string;
  updatedAt: string;
  channelID: string;
  styleId: string | null;
  mainLogo: string | null;
  stateLocation: string | null;
  actuations: number;
}

const fetchClients = async (): Promise<Client[]> => {
  const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/getAdminData`); // Replace with actual API endpoint
  return response.data;
};

const EditableTable: React.FC = () => {
  const { data = [], isLoading, error } = useQuery({
    queryKey: ['clients'],
    queryFn: fetchClients,
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'actuations', desc: true },
  ]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    ApiKey: false, // API Key column hidden by default
  });

  const handleCreateNewClient = () => {
    console.log('Feature to add new clients can be implemented here.');
  };

  const handleDeleteClient = (id: number) => {
    console.log(`Feature to delete client with ID ${id} can be implemented here.`);
  };

  const totalActuations = data.reduce((sum, client) => sum + client.actuations, 0);

  const columns: ColumnDef<Client>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
      cell: ({ getValue }) => <div className="px-2 py-1">{getValue() as string}</div>,
    },
    {
      header: 'Access Key',
      accessorKey: 'cuid',
      cell: ({ getValue }) => <div className="px-2 py-1">{getValue() as string}</div>,
    },
    {
      header: 'Actuations',
      accessorKey: 'actuations',
      cell: ({ getValue }) => <div className="px-2 py-1">{getValue() as number}</div>,
      sortingFn: 'basic',
      footer: () => <div className="px-2 py-1 font-bold">{totalActuations}</div>, // Add total for Actuations column
    },
    {
      header: 'Email',
      accessorKey: 'email',
      cell: ({ getValue }) => <div className="px-2 py-1">{getValue() as string}</div>,
    },
    {
      header: 'Integration ID',
      accessorKey: 'integrationId',
      cell: ({ getValue }) => <div className="px-2 py-1">{getValue() as number}</div>,
      sortingFn: 'basic',
    },
    {
      header: 'API Key',
      accessorKey: 'ApiKey',
      cell: ({ getValue }) => <div className="px-2 py-1">{getValue() as string}</div>,
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <button
          onClick={() => handleDeleteClient(row.original.id)}
          className="text-red-500 hover:text-red-700 focus:outline-none focus:ring focus:ring-red-200"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) return <p>Loading clients...</p>;
  if (error) return <p>Error fetching clients.</p>;

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">Client Management</h2>
      <button
        onClick={handleCreateNewClient}
        className="bg-blue-500 text-white px-6 py-2 rounded shadow-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
      >
        Create New Client
      </button>

      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-2">Column Visibility</h3>
        <div className="flex gap-4">
          {table.getAllLeafColumns().map((column) => (
            <label key={column.id} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={column.getIsVisible()}
                onChange={column.getToggleVisibilityHandler()}
                className="form-checkbox text-blue-600"
              />
              <span className="text-gray-700">{column.id}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="table-auto w-full border border-gray-300 rounded-md shadow-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-gray-100">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 cursor-pointer hover:bg-gray-200"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === 'asc' && ' 🔼'}
                    {header.column.getIsSorted() === 'desc' && ' 🔽'}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => (
              <tr
                key={row.id}
                className={`hover:bg-gray-100 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-4 py-2 border-b border-gray-300 text-sm text-gray-700"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            {table.getFooterGroups().map((footerGroup) => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map((footer) => (
                  <td
                    key={footer.id}
                    className="px-4 py-2 border-t border-gray-300 font-semibold text-gray-700"
                  >
                    {flexRender(footer.column.columnDef.footer, footer.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default EditableTable;